import React from 'react';
import { Field, Form, Formik } from 'formik';
import { InputField, withFieldProps, withLoading } from '@adjust/dashboard-components';

import { compose } from '@adjust/dashboard-core/lib/fp';
import {
  hasMaxLength,
  hasMinLength,
  isPasswordInvalid,
  isRequired,
  noWhitespace,
  sameAs,
  validate,
  ValidationFunctions,
} from '@adjust/dashboard-core/lib/validations';
import { EditUserPasswordValues } from '@react/Pages/AccountSettingsPage/UserDetails/EditUserPassword/types';

const MIN_LENGTH = 11;
const MAX_LENGTH = 128;

const validation = {
  password: [[isRequired, () => t('field.current-password.error')]] as ValidationFunctions[],
  // eslint-disable-next-line @typescript-eslint/naming-convention
  new_password: [
    [isRequired, () => t('field.new-password.error')],
    [noWhitespace, () => t('field.new-password.error')],
    [hasMinLength(MIN_LENGTH), () => t('field.password.error.too-short')],
    [hasMaxLength(MAX_LENGTH), () => t('field.password.error.too-long')],
    [isPasswordInvalid, () => t('field.password.error.invalid')],
  ] as ValidationFunctions[],
  confirmation: password =>
    [
      [isRequired, () => t('field.confirm-password.error')],
      [sameAs(password), () => t('field.confirm-password.error')],
    ] as ValidationFunctions[],
};

const validateConfirmation = compose(validate, validation.confirmation);

interface EditUserPasswordForm {
  values: EditUserPasswordValues;
  onFormSave: (value: EditUserPasswordValues) => void;
  onCancel: () => void;
}

const EditUserPasswordFormBase: React.FC<EditUserPasswordForm> = ({ onCancel, onFormSave, values }) => (
  <Formik initialValues={values} onSubmit={onFormSave}>
    {({ values, isValid, dirty }) => (
      <Form className="flex-one flex-box-column">
        <div className="flex-one content-inner">
          <Field name="password" validate={validate(validation.password)}>
            {withFieldProps(<InputField label={t('field.current-password')} type="password" />)}
          </Field>

          <p>{t('user-confirmation.password-requirements')}</p>

          <Field name="new_password" validate={validate(validation.new_password)}>
            {withFieldProps(<InputField label={t('field.new-password')} type="password" />)}
          </Field>

          <Field name="confirmation" validate={validateConfirmation(values.new_password)}>
            {withFieldProps(<InputField label={t('field.confirm-password')} type="password" />)}
          </Field>
        </div>

        <div className="inner-panel-footer flex-auto">
          <div className="flex-box-row">
            <button type="button" className="btn flex-one" onClick={onCancel}>
              {t('action.cancel')}
            </button>

            <button type="submit" className="btn btn-main flex-one" disabled={!isValid || !dirty}>
              {t('action.save')}
            </button>
          </div>
        </div>
      </Form>
    )}
  </Formik>
);

export const EditUserPasswordContent = withLoading(EditUserPasswordFormBase);
